import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Mail, CheckCircle, Star, Home, Package, Calendar, CreditCard, DollarSign, Clock, AlertCircle } from 'lucide-react';
import ProgressBar from '../components/Checkout/ProgressBar';
import api, { orderAPI } from '../services/api';
import { useNotificationHook } from '../hooks/useNotificationHook';
import { mapPaymentMethodToDisplay, mapPaymentStatusToDisplay, mapOrderStatusToDisplay, getStatusColor } from '../utils/paymentUtils';

// Component to fetch and display original order details with EMI calculations
const OriginalOrderDetails = ({ paymentData, formatPrice, orderDetails }) => {
  const [originalOrderData, setOriginalOrderData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOriginalOrder = async () => {
      console.log('🔍 Starting fetchOriginalOrder with paymentData:', paymentData);
      
      if (!paymentData?.order_id && !paymentData?.order_number) {
        console.log('❌ No order_id or order_number found in paymentData');
        setLoading(false);
        return;
      }

      try {
        let response = null;
        let attempts = [];
        
        // Try to fetch by order_id first
        if (paymentData.order_id) {
          console.log('🔍 Attempt 1: Fetching original order by ID:', paymentData.order_id);
          try {
            response = await orderAPI.getById(paymentData.order_id);
            attempts.push(`orderAPI.getById(${paymentData.order_id}): ${response?.data?.success ? 'SUCCESS' : 'FAILED'}`);
            console.log('🔍 Response from orderAPI.getById:', response);
          } catch (err) {
            attempts.push(`orderAPI.getById(${paymentData.order_id}): ERROR - ${err.message}`);
            console.error('❌ Error with orderAPI.getById:', err);
          }
        }
        
        // Try direct API call to orders endpoint
        if (!response?.data?.success && paymentData.order_id) {
          console.log('🔍 Attempt 2: Direct API call to /orders/{id}:', paymentData.order_id);
          try {
            response = await api.get(`/orders/${paymentData.order_id}`);
            attempts.push(`/orders/${paymentData.order_id}: ${response?.data?.success ? 'SUCCESS' : 'FAILED'}`);
            console.log('🔍 Response from direct API call:', response);
          } catch (err) {
            attempts.push(`/orders/${paymentData.order_id}: ERROR - ${err.message}`);
            console.error('❌ Error with direct API call:', err);
          }
        }
        
        // If that fails, try different API endpoints for order_number
        if (!response?.data?.success && paymentData.order_number) {
          const endpoints = [
            `/orders?order_number=${paymentData.order_number}`,
            `/orders/by-number/${paymentData.order_number}`,
            `/orders/${paymentData.order_number}`,
            `/orders/search?order_number=${paymentData.order_number}`
          ];
          
          for (const endpoint of endpoints) {
            try {
              console.log(`🔍 Attempt: Fetching from ${endpoint}`);
              response = await api.get(endpoint);
              attempts.push(`${endpoint}: ${response?.data?.success ? 'SUCCESS' : 'FAILED'}`);
              console.log(`🔍 Response from ${endpoint}:`, response);
              if (response?.data?.success) break;
            } catch (err) {
              attempts.push(`${endpoint}: ERROR - ${err.message}`);
              console.error(`❌ Error with ${endpoint}:`, err);
            }
          }
        }
        
        // Try user's order list as final fallback
        if (!response?.data?.success) {
          console.log('🔍 Final attempt: Fetching from user orders list');
          try {
            const userOrdersResponse = await api.get('/orders/my-orders');
            attempts.push(`/orders/my-orders: ${userOrdersResponse?.data?.success ? 'SUCCESS' : 'FAILED'}`);
            console.log('🔍 User orders response:', userOrdersResponse);
            
            if (userOrdersResponse?.data?.success) {
              const userOrders = userOrdersResponse.data.data;
              const matchingOrder = userOrders.find(order => 
                order.id === paymentData.order_id || 
                order.order_number === paymentData.order_number
              );
              
              if (matchingOrder) {
                console.log('✅ Found matching order in user orders:', matchingOrder);
                response = { data: { success: true, data: matchingOrder } };
                attempts.push(`Found in user orders: SUCCESS`);
              } else {
                attempts.push(`Found in user orders: NO_MATCH`);
                console.log('❌ No matching order found in user orders. Available orders:', 
                  userOrders.map(o => ({ id: o.id, order_number: o.order_number })));
              }
            }
          } catch (err) {
            attempts.push(`/orders/my-orders: ERROR - ${err.message}`);
            console.error('❌ Error fetching user orders:', err);
          }
        }
        
        console.log('🔍 All fetch attempts:', attempts);
        
        if (response?.data?.success) {
          const orderData = Array.isArray(response.data.data) ? response.data.data[0] : response.data.data;
          setOriginalOrderData(orderData);
          console.log('✅ Original order fetched successfully:', orderData);
        } else {
          console.log('❌ Could not fetch original order after all attempts');
          console.log('❌ Available paymentData:', paymentData);
        }
      } catch (error) {
        console.error('❌ Unexpected error in fetchOriginalOrder:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchOriginalOrder();
  }, [paymentData?.order_id, paymentData?.order_number]);

  if (loading) {
    return (
      <div className="flex justify-between items-center p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
        <span className="font-medium text-blue-700 dark:text-blue-300">Original Total Amount:</span>
        <span className="text-lg font-semibold text-blue-900 dark:text-blue-100">Loading...</span>
      </div>
    );
  }

  // Use original order data if available - prioritize original_total_amount
  const originalAmount = originalOrderData?.original_total_amount || 
                         originalOrderData?.total_amount || 
                         0;

  console.log('🔍 Original Order Details Debug:', {
    originalOrderData,
    originalOrderTotalAmount: originalOrderData?.total_amount,
    originalOrderOriginalTotal: originalOrderData?.original_total_amount,
    selectedAmount: originalAmount,
    orderReference: paymentData?.order_number
  });

  // Calculate EMI amounts using the correct original data
  const advanceAmount = parseFloat(paymentData?.paid_amount || 0);
  const totalEmiAmount = originalAmount - advanceAmount;
  const currentInstallmentAmount = parseFloat(orderDetails?.total || 0);

  console.log('🔍 EMI Calculation in OriginalOrderDetails:', {
    originalAmount,
    advanceAmount,
    totalEmiAmount,
    currentInstallmentAmount
  });

  return originalAmount > 0 ? (
    <>
      {/* Original Total Amount */}
      <div className="flex justify-between items-center p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
        <span className="font-medium text-blue-700 dark:text-blue-300">Original Total Amount:</span>
        <span className="text-lg font-semibold text-blue-900 dark:text-blue-100">
          {formatPrice(originalAmount)}
          <span className="text-xs ml-2 opacity-70">
            (Order #{paymentData?.order_number || 'N/A'})
          </span>
        </span>
      </div>

      {/* Advance Amount */}
      {advanceAmount > 0 && (
        <div className="flex justify-between items-center p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
          <span className="font-medium text-green-700 dark:text-green-300">Advance Amount:</span>
          <span className="text-lg font-semibold text-green-900 dark:text-green-100">
            {formatPrice(advanceAmount)}
          </span>
        </div>
      )}

      {/* Total EMI Amount */}
      {totalEmiAmount > 0 && (
        <div className="flex justify-between items-center p-3 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg">
          <span className="font-medium text-indigo-700 dark:text-indigo-300">Total EMI Amount:</span>
          <span className="text-lg font-semibold text-indigo-900 dark:text-indigo-100">
            {formatPrice(Math.max(0, totalEmiAmount))}
          </span>
        </div>
      )}

      {/* Remaining Amount After This Payment */}
      {(() => {
        let remainingAmount = 0;
        
        if (paymentData?.remaining_amount) {
          // Subtract current payment from the remaining amount to get balance after this payment
          remainingAmount = parseFloat(paymentData.remaining_amount) - currentInstallmentAmount;
          remainingAmount = Math.max(0, remainingAmount);
        } else {
          // Fallback calculation: Total EMI Amount - Current Installment Amount
          remainingAmount = Math.max(0, totalEmiAmount - currentInstallmentAmount);
        }

        return remainingAmount > 0 ? (
          <div className="flex justify-between items-center p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
            <span className="font-medium text-orange-700 dark:text-orange-300">Remaining Amount:</span>
            <span className="text-lg font-semibold text-orange-900 dark:text-orange-100">
              {formatPrice(remainingAmount)}
            </span>
          </div>
        ) : (
          <div className="flex justify-between items-center p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
            <span className="font-medium text-green-700 dark:text-green-300">Remaining Amount:</span>
            <span className="text-lg font-semibold text-green-900 dark:text-green-100">
              ₹0.00 (Fully Paid)
            </span>
          </div>
        );
      })()}
    </>
  ) : (
    <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
      <div className="flex justify-between items-center">
        <span className="font-medium text-yellow-700 dark:text-yellow-300">Original Total Amount:</span>
        <span className="text-lg font-semibold text-yellow-900 dark:text-yellow-100">
          Order not found
        </span>
      </div>
      <div className="mt-2 text-xs text-yellow-600 dark:text-yellow-400">
        Debug: Order ID: {paymentData?.order_id || 'None'} | 
        Order Number: {paymentData?.order_number || 'None'}
        {originalOrderData === null && ' | Check console for fetch attempts'}
      </div>
    </div>
  );
};

const ThankYou = () => {
  const { notify } = useNotificationHook();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [orderDetails, setOrderDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [review, setReview] = useState('');
  const [isEMIInstallment, setIsEMIInstallment] = useState(false);
  const [isSplitPayment, setIsSplitPayment] = useState(false);
  const [splitPaymentType, setSplitPaymentType] = useState('');
  const [splitPaymentText, setSplitPaymentText] = useState('');

  useEffect(() => {
    const fetchOrderDetails = async () => {
      try {
        setLoading(true);
        
        // First try to get order from localStorage (for immediate display)
    const savedOrderDetails = localStorage.getItem('orderDetails');
    if (savedOrderDetails) {
      const parsedOrderDetails = JSON.parse(savedOrderDetails);
      setOrderDetails(parsedOrderDetails);
      
      // Check if this is an EMI installment payment
      if (parsedOrderDetails.isEMIInstallment) {
        setIsEMIInstallment(true);
        console.log('✅ EMI installment payment detected');
      }

      // Check if this is a Split Payment
      if (parsedOrderDetails.isSplitPayment) {
        setIsSplitPayment(true);
        setSplitPaymentType(parsedOrderDetails.splitPaymentType || '');
        setSplitPaymentText(parsedOrderDetails.splitPaymentText || '');
        console.log('✅ Split Payment detected:', {
          type: parsedOrderDetails.splitPaymentType,
          text: parsedOrderDetails.splitPaymentText
        });
      }
      
      setLoading(false); // Set loading to false immediately if we have localStorage data
      
      // Clear orderDetails after a delay to prevent redirect issues
      setTimeout(() => {
        localStorage.removeItem('orderDetails');
      }, 5000); // Clear after 5 seconds
    }

        // Then try to get order number from URL params
        const orderId = searchParams.get('order_id');
        const orderNumber = searchParams.get('order_number');
        
        if (orderNumber) {
          // Fetch real-time order details from database
          const response = await api.get(`/orders/${orderNumber}`);
          if (response.data.success) {
            const dbOrderDetails = response.data.data;
            
            // Update order details with real-time data
            const updatedOrderDetails = {
              orderNumber: dbOrderDetails.order_number,
              paymentMethod: dbOrderDetails.payment_method,
              total: parseFloat(dbOrderDetails.total_amount),
              items: dbOrderDetails.items || [],
              date: dbOrderDetails.created_at,
              paymentStatus: dbOrderDetails.payment_status,
              orderStatus: dbOrderDetails.order_status,
              paymentId: dbOrderDetails.payment_id,
              orderId: dbOrderDetails.id,
              customerName: dbOrderDetails.customer_name,
              customerEmail: dbOrderDetails.customer_email,
              customerPhone: dbOrderDetails.customer_phone,
              billingAddress: dbOrderDetails.billing_address,
              notes: dbOrderDetails.notes,
              appliedCoupon: dbOrderDetails.applied_coupon || null,
              payment_option: dbOrderDetails.payment_option,
              payment_option_details: dbOrderDetails.payment_option_details,
              original_total_amount: dbOrderDetails.original_total_amount,
              advance_amount: dbOrderDetails.advance_amount,
              remaining_amount: dbOrderDetails.remaining_amount
            };
            
            setOrderDetails(updatedOrderDetails);
            
                    // Update localStorage with real-time data
        localStorage.setItem('orderDetails', JSON.stringify(updatedOrderDetails));
        
        // Clear orderDetails after a delay to prevent redirect issues
        setTimeout(() => {
          localStorage.removeItem('orderDetails');
        }, 5000); // Clear after 5 seconds
      }
    } else if (orderId) {
          // If we have order_id but no order_number, try to fetch by order_id
          const response = await api.get(`/orders/${orderId}`);
          if (response.data.success) {
            const dbOrderDetails = response.data.data;
            
            const updatedOrderDetails = {
              orderNumber: dbOrderDetails.order_number,
              paymentMethod: dbOrderDetails.payment_method,
              total: parseFloat(dbOrderDetails.total_amount),
              items: dbOrderDetails.items || [],
              date: dbOrderDetails.created_at,
              paymentStatus: dbOrderDetails.payment_status,
              orderStatus: dbOrderDetails.order_status,
              paymentId: dbOrderDetails.payment_id,
              orderId: dbOrderDetails.id,
              customerName: dbOrderDetails.customer_name,
              customerEmail: dbOrderDetails.customer_email,
              customerPhone: dbOrderDetails.customer_phone,
              billingAddress: dbOrderDetails.billing_address,
              notes: dbOrderDetails.notes,
              appliedCoupon: dbOrderDetails.applied_coupon || null,
              payment_option: dbOrderDetails.payment_option,
              payment_option_details: dbOrderDetails.payment_option_details,
              original_total_amount: dbOrderDetails.original_total_amount,
              advance_amount: dbOrderDetails.advance_amount,
              remaining_amount: dbOrderDetails.remaining_amount
            };
            
            setOrderDetails(updatedOrderDetails);
            localStorage.setItem('orderDetails', JSON.stringify(updatedOrderDetails));
          }
        }
      } catch (error) {
        console.error('Error fetching order details:', error);
        // If API fails, keep the localStorage data
      } finally {
        setLoading(false);
      }
    };

    fetchOrderDetails();
  }, [searchParams]);

  const formatPrice = (price) => {
    // Handle undefined, null, or invalid price values
    const numericPrice = parseFloat(price) || 0;
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(numericPrice);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPaymentStatusInfo = (status, paymentMethod) => {
    // Special handling for EMI installment payments
    if (isEMIInstallment) {
      return {
        icon: CheckCircle,
        color: 'text-green-600',
        bgColor: 'bg-green-100 dark:bg-green-900/20',
        title: 'EMI Installment Payment Completed',
        description: 'Your EMI installment payment has been successfully processed.'
      };
    }
    
    switch (status?.toLowerCase()) {
      case 'pending':
        return {
          icon: Clock,
          color: 'text-yellow-600',
          bgColor: 'bg-yellow-100 dark:bg-yellow-900/20',
          title: 'Payment Pending',
          description: `Your ${paymentMethod} payment is being processed. We'll confirm once payment is received.`
        };
      case 'processing':
        return {
          icon: CreditCard,
          color: 'text-blue-600',
          bgColor: 'bg-blue-100 dark:bg-blue-900/20',
          title: 'Payment Processing',
          description: 'Your payment is being processed. You will receive confirmation shortly.'
        };
      case 'completed':
        return {
          icon: CheckCircle,
          color: 'text-green-600',
          bgColor: 'bg-green-100 dark:bg-green-900/20',
          title: 'Payment Completed',
          description: 'Your payment has been successfully processed.'
        };
      case 'failed':
        return {
          icon: AlertCircle,
          color: 'text-red-600',
          bgColor: 'bg-red-100 dark:bg-red-900/20',
          title: 'Payment Failed',
          description: 'Your payment could not be processed. Please try again.'
        };
      case 'refunded':
        return {
          icon: AlertCircle,
          color: 'text-orange-600',
          bgColor: 'bg-orange-100 dark:bg-orange-900/20',
          title: 'Payment Refunded',
          description: 'Your payment has been refunded.'
        };
      case 'cancelled':
        return {
          icon: AlertCircle,
          color: 'text-gray-600',
          bgColor: 'bg-gray-100 dark:bg-gray-900/20',
          title: 'Payment Cancelled',
          description: 'Your payment was cancelled. You can try again or choose a different payment method.'
        };
      default:
        return {
          icon: CheckCircle,
          color: 'text-green-600',
          bgColor: 'bg-green-100 dark:bg-green-900/20',
          title: 'Order Placed',
          description: 'Your order has been successfully placed.'
        };
    }
  };

  const handleRatingSubmit = async () => {
    if (rating === 0) {
      notify.warning('Please select a rating before submitting', 'Rating Required');
      return;
    }

    try {
      const response = await api.post(`/orders/${orderDetails.orderNumber}/rating`, {
        rating,
        review: review.trim() || null
      });

      if (response.data.success) {
        notify.success('Thank you for your rating!', 'Rating Submitted');
        setRating(0);
        setReview('');
        
        // Update orderDetails to include the rating
        setOrderDetails(prev => ({
          ...prev,
          rating,
          review: review.trim() || null,
          review_date: new Date().toISOString()
        }));
      }
    } catch (error) {
      console.error('Error submitting rating:', error);
      const errorMessage = error.response?.data?.message || 'Failed to submit rating. Please try again.';
      notify.error(errorMessage, 'Rating Error');
    }
  };

  const handleBackToHome = () => {
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-white via-blue-50 to-indigo-50 dark:from-slate-900 dark:via-purple-900 dark:to-slate-900">
        <ProgressBar currentStep="thankyou" />
        <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12 py-16">
          <div className="text-center">
            <div className="w-32 h-32 mx-auto mb-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center shadow-lg">
              <div className="animate-spin rounded-full h-16 w-16 border-4 border-white border-t-transparent"></div>
            </div>
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-6">
              Loading Order Details...
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300">
              Please wait while we fetch your order information.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!orderDetails) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-white via-blue-50 to-indigo-50 dark:from-slate-900 dark:via-purple-900 dark:to-slate-900">
        <ProgressBar currentStep="thankyou" />
        <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12 py-16">
          <div className="text-center">
            <div className="w-32 h-32 mx-auto mb-8 bg-gradient-to-br from-red-500 to-pink-600 rounded-full flex items-center justify-center shadow-lg">
              <AlertCircle className="w-16 h-16 text-white" />
            </div>
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-6">
              Order not found
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 mb-12">
              Please complete a purchase to view order details.
            </p>
            <button
              onClick={handleBackToHome}
              className="card-3d bg-gradient-to-r from-blue-600 to-purple-600 text-white px-10 py-4 rounded-2xl font-bold hover:scale-105 transition-all duration-300 shadow-glow"
            >
              Back to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Safety check for orderDetails
  if (!orderDetails) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-white via-blue-50 to-indigo-50 dark:from-slate-900 dark:via-purple-900 dark:to-slate-900">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12 py-16">
          <div className="text-center">
            <div className="w-32 h-32 mx-auto mb-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center shadow-lg">
              <div className="animate-spin rounded-full h-16 w-16 border-4 border-white border-t-transparent"></div>
            </div>
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-6">Loading Order Details...</h2>
            <p className="text-xl text-gray-600 dark:text-gray-300">Please wait while we fetch your order information.</p>
          </div>
        </div>
      </div>
    );
  }

  const paymentStatusInfo = getPaymentStatusInfo(orderDetails.paymentStatus, orderDetails.paymentMethod);
  const StatusIcon = paymentStatusInfo.icon;

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-blue-50 to-indigo-50 dark:from-slate-900 dark:via-purple-900 dark:to-slate-900">
      <ProgressBar currentStep="thankyou" />
      
      <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 py-16">
        {/* Success Message */}
        <div className="text-center mb-12">
          <div className={`w-32 h-32 mx-auto mb-8 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center shadow-glow animate-bounce`}>
            <StatusIcon className="w-16 h-16 text-white" />
          </div>
          <h1 className="text-5xl font-bold text-gray-900 dark:text-white mb-6">
            {paymentStatusInfo.title}
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
            {paymentStatusInfo.description}
          </p>
        </div>

        {/* EMI Installment Payment Success Message */}
        {isEMIInstallment && (
          <div className="card-3d bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 backdrop-blur-md rounded-3xl shadow-xl border border-green-200 dark:border-green-800 p-8 mb-12">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center shadow-lg">
                <CheckCircle className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-3xl font-bold text-green-800 dark:text-green-200">
                EMI Installment Payment Successful!
              </h2>
            </div>
            <p className="text-lg text-green-700 dark:text-green-300 mb-6 leading-relaxed">
              Your EMI installment payment has been processed successfully. The payment has been applied to your existing EMI plan.
            </p>
            <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-2xl shadow-lg border border-white/20 p-6">
              <div className="space-y-4">
                <div className="flex justify-between items-center py-2 border-b border-green-200 dark:border-green-700">
                  <span className="text-green-700 dark:text-green-300 font-semibold">Payment Amount:</span>
                  <span className="text-xl font-bold text-green-900 dark:text-green-100">
                    ₹{orderDetails.total?.toFixed(2) || '0.00'}
                  </span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-green-200 dark:border-green-700">
                  <span className="text-green-700 dark:text-green-300 font-semibold">Payment Method:</span>
                  <span className="text-green-900 dark:text-green-100 font-semibold">{orderDetails.paymentMethod}</span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-green-700 dark:text-green-300 font-semibold">Order Number:</span>
                  <span className="text-xl font-bold text-green-900 dark:text-green-100">{orderDetails.orderNumber}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Split Payment Success Message */}
        {isSplitPayment && (
          <div className="card-3d bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 backdrop-blur-md rounded-3xl shadow-xl border border-blue-200 dark:border-blue-800 p-8 mb-12">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center shadow-lg">
                <CheckCircle className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-3xl font-bold text-blue-800 dark:text-blue-200">
                Split Payment Successful!
              </h2>
            </div>
            <p className="text-lg text-blue-700 dark:text-blue-300 mb-6 leading-relaxed">
              Your {splitPaymentText || 'Split Payment'} has been processed successfully. The payment has been applied to your existing order.
            </p>
            <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-2xl shadow-lg border border-white/20 p-6">
              <div className="space-y-4">
                <div className="flex justify-between items-center py-2 border-b border-blue-200 dark:border-blue-700">
                  <span className="text-blue-700 dark:text-blue-300 font-semibold">Payment Amount:</span>
                  <span className="text-xl font-bold text-blue-900 dark:text-blue-100">
                    ₹{orderDetails.total?.toFixed(2) || '0.00'}
                  </span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-blue-200 dark:border-blue-700">
                  <span className="text-blue-700 dark:text-blue-300 font-semibold">Payment Type:</span>
                  <span className="text-blue-900 dark:text-blue-100 font-semibold">{splitPaymentText || 'Split Payment'}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-blue-200 dark:border-blue-700">
                  <span className="text-blue-700 dark:text-blue-300 font-semibold">Payment Method:</span>
                  <span className="text-blue-900 dark:text-blue-100 font-semibold">{orderDetails.paymentMethod}</span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-blue-700 dark:text-blue-300 font-semibold">Order Number:</span>
                  <span className="text-xl font-bold text-blue-900 dark:text-blue-100">{orderDetails.orderNumber}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Order Details Card */}
          <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-3xl shadow-xl border border-white/20 p-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center shadow-lg">
                <Package className="w-6 h-6 text-white" />
              </div>
              Order Details
            </h2>
            <div className="space-y-6">
              <div className="flex justify-between items-center py-3 border-b border-gray-200 dark:border-gray-600">
                <span className="text-gray-600 dark:text-gray-400 font-semibold">Order Number:</span>
                <span className="font-bold text-gray-900 dark:text-white text-lg">{orderDetails.orderNumber}</span>
              </div>
              <div className="flex justify-between items-center py-3 border-b border-gray-200 dark:border-gray-600">
                <span className="text-gray-600 dark:text-gray-400 font-semibold">Order Date:</span>
                <span className="text-gray-900 dark:text-white font-semibold">{formatDate(orderDetails.date)}</span>
              </div>
              <div className="flex justify-between items-center py-3 border-b border-gray-200 dark:border-gray-600">
                <span className="text-gray-600 dark:text-gray-400 font-semibold">Payment Method:</span>
                <span className="text-gray-900 dark:text-white font-semibold">{orderDetails.paymentMethod}</span>
              </div>
              <div className="flex justify-between items-center py-3 border-b border-gray-200 dark:border-gray-600">
                <span className="text-gray-600 dark:text-gray-400 font-semibold">Payment Status:</span>
                <span className={`px-4 py-2 rounded-full text-sm font-bold ${
                  orderDetails.paymentStatus === 'pending' 
                    ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400'
                    : orderDetails.paymentStatus === 'processing'
                    ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400'
                    : orderDetails.paymentStatus === 'completed'
                    ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                    : orderDetails.paymentStatus === 'failed'
                    ? 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                    : orderDetails.paymentStatus === 'refunded'
                    ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400'
                    : 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400'
                }`}>
                  {mapPaymentStatusToDisplay(orderDetails.paymentStatus)}
                </span>
              </div>
              <div className="flex justify-between items-center py-3 border-b border-gray-200 dark:border-gray-600">
                <span className="text-gray-600 dark:text-gray-400 font-semibold">Order Status:</span>
                <span className={`px-4 py-2 rounded-full text-sm font-bold ${getStatusColor(orderDetails.orderStatus, 'order')}`}>
                  {mapOrderStatusToDisplay(orderDetails.orderStatus)}
                </span>
              </div>
              {orderDetails.offlineProof && (
                <div className="flex justify-between items-center py-3 border-b border-gray-200 dark:border-gray-600">
                  <span className="text-gray-600 dark:text-gray-400 font-semibold">Payment Proof:</span>
                  <span className="text-gray-900 dark:text-white font-semibold">{orderDetails.offlineProof}</span>
                </div>
              )}
              
              {/* Coupon Information */}
              {orderDetails.appliedCoupon && (
                <div className="border-t-2 border-gray-200 dark:border-gray-700 pt-6">
                  <h4 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Coupon Applied:</h4>
                  <div className="card-3d bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 backdrop-blur-md rounded-2xl shadow-lg border border-green-200 dark:border-green-800 p-4">
                    <div className="flex justify-between items-center">
                      <div>
                        <span className="text-lg font-bold text-green-700 dark:text-green-400">
                          {orderDetails.appliedCoupon.code}
                        </span>
                        <p className="text-sm text-green-600 dark:text-green-300 mt-1">
                          {orderDetails.appliedCoupon.description || 'Discount applied'}
                        </p>
                      </div>
                      <div className="text-right">
                        {orderDetails.appliedCoupon.calculated_discount_amount ? (
                          <span className="text-xl font-bold text-green-600 dark:text-green-400">
                            -{formatPrice(orderDetails.appliedCoupon.calculated_discount_amount)}
                          </span>
                        ) : orderDetails.appliedCoupon.discount_amount ? (
                          <span className="text-xl font-bold text-green-600 dark:text-green-400">
                            -{formatPrice(orderDetails.appliedCoupon.discount_amount)}
                          </span>
                        ) : orderDetails.appliedCoupon.discount_percentage ? (
                          <span className="text-xl font-bold text-green-600 dark:text-green-400">
                            -{orderDetails.appliedCoupon.discount_percentage}%
                          </span>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Discount Details */}
              {orderDetails.items && orderDetails.items.length > 0 && orderDetails.items.some(item => 
                item.regular_license_discount > 0 || 
                item.extended_license_discount > 0 || 
                item.extended_support_discount > 0 ||
                (item.addons && item.addons.some(addon => addon.discount_percentage > 0))
              ) && (
                <div className="border-t-2 border-gray-200 dark:border-gray-700 pt-6">
                  <h4 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Discount Details:</h4>
                  <div className="card-3d bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 backdrop-blur-md rounded-2xl shadow-lg border border-purple-200 dark:border-purple-800 p-4">
                    <div className="space-y-3">
                      {orderDetails.items.map((item, index) => {
                        const hasDiscount = item.regular_license_discount > 0 || 
                                          item.extended_license_discount > 0 || 
                                          item.extended_support_discount > 0 ||
                                          (item.addons && item.addons.some(addon => addon.discount_percentage > 0));
                        
                        if (!hasDiscount) return null;
                        
                        return (
                          <div key={index} className="text-sm text-purple-700 dark:text-purple-300">
                            <span className="font-bold">{item.product_name}:</span>
                            {item.regular_license_discount > 0 && (
                              <span className="ml-2">Regular License: {item.regular_license_discount}% OFF</span>
                            )}
                            {item.extended_license_discount > 0 && (
                              <span className="ml-2">Extended License: {item.extended_license_discount}% OFF</span>
                            )}
                            {item.extended_support_discount > 0 && (
                              <span className="ml-2">Extended Support: {item.extended_support_discount}% OFF</span>
                            )}
                            {item.addons && item.addons.map((addon, addonIndex) => (
                              addon.discount_percentage > 0 && (
                                <span key={addonIndex} className="ml-2">{addon.name}: {addon.discount_percentage}% OFF</span>
                              )
                            ))}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
              
              {/* Show subtotal for split payments with coupon */}
              {orderDetails.isSplitPayment && orderDetails.appliedCoupon && (
                <div className="border-t-2 border-gray-200 dark:border-gray-700 pt-6">
                  <div className="space-y-3">
                    <div className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-gray-600">
                      <span className="text-gray-600 dark:text-gray-400 font-semibold">Subtotal:</span>
                      <span className="font-bold text-gray-900 dark:text-white text-lg">
                        {formatPrice(orderDetails.total + (orderDetails.appliedCoupon.calculated_discount_amount || 0))}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-2">
                      <span className="text-gray-600 dark:text-gray-400 font-semibold">Coupon Discount:</span>
                      <span className="font-bold text-green-600 dark:text-green-400 text-lg">
                        -{formatPrice(orderDetails.appliedCoupon.calculated_discount_amount || 0)}
                      </span>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="border-t-2 border-blue-200 dark:border-blue-700 pt-6">
                <div className="flex justify-between items-center">
                  <span className="text-2xl font-bold text-gray-900 dark:text-white">Total Amount:</span>
                  <span className="text-3xl font-bold text-gray-900 dark:text-white">{formatPrice(orderDetails.total)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Order Items Card */}
          <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-3xl shadow-xl border border-white/20 p-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center shadow-lg">
                <Package className="w-6 h-6 text-white" />
              </div>
              Order Items
            </h2>
            <div className="space-y-6">
              {/* Check if this is an installment payment */}
              {orderDetails.payment_option === 'installment' && orderDetails.payment_option_details?.isInstallmentPayment ? (
                /* Display EMI/Split Payment Installment Details */
                <div className="card-3d bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-900/20 dark:to-amber-900/20 backdrop-blur-md rounded-2xl shadow-lg border border-orange-200 dark:border-orange-800 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-amber-600 rounded-full flex items-center justify-center shadow-lg">
                        <CreditCard className="w-5 h-5 text-white" />
                      </div>
                      <h3 className="text-lg font-bold text-orange-900 dark:text-orange-100">
                        {orderDetails.payment_option_details.paymentData?.type === 'emi_payment' ? 'EMI Payment' : 'Split Payment'} Installment
                      </h3>
                    </div>
                    <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-bold bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-200 shadow-lg">
                      Installment {orderDetails.payment_option_details.paymentData?.current_installment || 1} of {orderDetails.payment_option_details.paymentData?.total_installments || 1}
                    </span>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex justify-between items-center py-2 border-b border-orange-200 dark:border-orange-700">
                      <span className="text-sm font-semibold text-orange-700 dark:text-orange-300">Order Reference:</span>
                      <span className="text-sm font-bold text-orange-900 dark:text-orange-100">
                        #{orderDetails.payment_option_details.paymentData?.order_number}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-orange-200 dark:border-orange-700">
                      <span className="text-sm font-semibold text-orange-700 dark:text-orange-300">Payment Amount:</span>
                      <span className="text-sm font-bold text-orange-900 dark:text-orange-100">
                        {formatPrice(orderDetails.payment_option_details.paymentData?.amount || 0)}
                      </span>
                    </div>
                    {(orderDetails.payment_option_details.paymentData?.payment_date || orderDetails.payment_option_details.paymentData?.next_payment_date) && (
                      <div className="flex justify-between items-center py-2">
                        <span className="text-sm font-semibold text-orange-700 dark:text-orange-300">Installment Due Date:</span>
                        <span className="text-sm font-bold text-orange-900 dark:text-orange-100">
                          {formatDate(orderDetails.payment_option_details.paymentData.payment_date || orderDetails.payment_option_details.paymentData.next_payment_date)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ) : orderDetails.isSplitPayment ? (
                /* Display Split Payment Details */
                <div className="card-3d bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 backdrop-blur-md rounded-2xl shadow-lg border border-blue-200 dark:border-blue-800 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center shadow-lg">
                        <CreditCard className="w-5 h-5 text-white" />
                      </div>
                      <h3 className="text-lg font-bold text-blue-900 dark:text-blue-100">
                        Split Payment - {orderDetails.splitPaymentText || 'Remaining Amount'}
                      </h3>
                    </div>
                    <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-bold bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200 shadow-lg">
                      Split Payment
                    </span>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex justify-between items-center py-2 border-b border-blue-200 dark:border-blue-700">
                      <span className="text-sm font-semibold text-blue-700 dark:text-blue-300">Payment Type:</span>
                      <span className="text-sm font-bold text-blue-900 dark:text-blue-100">
                        {orderDetails.splitPaymentText || 'Split Payment'}
                      </span>
                    </div>
                    {orderDetails.appliedCoupon ? (
                      <>
                        <div className="flex justify-between items-center py-2 border-b border-blue-200 dark:border-blue-700">
                          <span className="text-sm font-semibold text-blue-700 dark:text-blue-300">Original Amount:</span>
                          <span className="text-sm font-bold text-blue-900 dark:text-blue-100">
                            {formatPrice(orderDetails.total + (orderDetails.appliedCoupon.calculated_discount_amount || 0))}
                          </span>
                        </div>
                        <div className="flex justify-between items-center py-2 border-b border-blue-200 dark:border-blue-700">
                          <span className="text-sm font-semibold text-blue-700 dark:text-blue-300">Coupon Discount:</span>
                          <span className="text-sm font-bold text-green-600 dark:text-green-400">
                            -{formatPrice(orderDetails.appliedCoupon.calculated_discount_amount || 0)}
                          </span>
                        </div>
                        <div className="flex justify-between items-center py-2 border-b border-blue-200 dark:border-blue-700">
                          <span className="text-sm font-semibold text-blue-700 dark:text-blue-300">Final Amount:</span>
                          <span className="text-sm font-bold text-blue-900 dark:text-blue-100">
                            {formatPrice(orderDetails.total || 0)}
                          </span>
                        </div>
                      </>
                    ) : (
                      <div className="flex justify-between items-center py-2 border-b border-blue-200 dark:border-blue-700">
                        <span className="text-sm font-semibold text-blue-700 dark:text-blue-300">Payment Amount:</span>
                        <span className="text-sm font-bold text-blue-900 dark:text-blue-100">
                          {formatPrice(orderDetails.total || 0)}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between items-center py-2">
                      <span className="text-sm font-semibold text-blue-700 dark:text-blue-300">Payment Method:</span>
                      <span className="text-sm font-bold text-blue-900 dark:text-blue-100">
                        {orderDetails.paymentMethod}
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                /* Display Regular Product Items */
                orderDetails.items && orderDetails.items.length > 0 ? orderDetails.items.map((item, index) => (
                <div key={index} className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-2xl shadow-lg border border-white/20 p-6">
                  {/* Main Product */}
                  <div className="flex justify-between items-start py-3 border-b border-gray-200 dark:border-gray-600">
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">{item.product_name || item.name}</h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400 font-semibold">Quantity: {item.quantity || 1}</p>
                      
                      {/* License Type with Original Price */}
                      {item.selectedLicense && (
                        <div className="mt-3">
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-bold bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg">
                            License: {item.selectedLicense === 'regular' ? 'Regular' : 'Extended'}
                          </span>
                          {item.selectedLicense === 'regular' && item.regular_license_original_price && (
                            <div className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                              <span className="line-through">{formatPrice(item.regular_license_original_price)}</span>
                              {item.regular_license_discount > 0 && (
                                <span className="ml-2 text-green-600 dark:text-green-400 font-semibold">{item.regular_license_discount}% OFF</span>
                              )}
                            </div>
                          )}
                          {item.selectedLicense === 'extended' && item.extended_license_original_price && (
                            <div className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                              <span className="line-through">{formatPrice(item.extended_license_original_price)}</span>
                              {item.extended_license_discount > 0 && (
                                <span className="ml-2 text-green-600 dark:text-green-400 font-semibold">{item.extended_license_discount}% OFF</span>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="text-right ml-4">
                      <span className="text-xl font-bold text-gray-900 dark:text-white">
                        {formatPrice(item.price || 0)}
                      </span>
                      {/* Show original price for main product if different */}
                      {item.selectedLicense === 'regular' && item.regular_license_original_price && item.regular_license_original_price !== item.price && (
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          <span className="line-through">{formatPrice(item.regular_license_original_price)}</span>
                        </div>
                      )}
                      {item.selectedLicense === 'extended' && item.extended_license_original_price && item.extended_license_original_price !== item.price && (
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          <span className="line-through">{formatPrice(item.extended_license_original_price)}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Extended Support with Price */}
                  {item.extendedSupport && (
                    <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600">
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-3">
                          <div className="w-3 h-3 bg-gradient-to-r from-green-500 to-emerald-600 rounded-full shadow-lg"></div>
                          <span className="text-sm font-bold text-green-700 dark:text-green-400">
                            Extended Support
                          </span>
                        </div>
                        <div className="text-right">
                          <span className="text-lg font-bold text-gray-900 dark:text-white">
                            {formatPrice(item.extended_support_price || 0)}
                          </span>
                          {item.extended_support_original_price && item.extended_support_original_price !== item.extended_support_price && (
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              <span className="line-through">{formatPrice(item.extended_support_original_price)}</span>
                              {item.extended_support_discount > 0 && (
                                <span className="ml-1 text-green-600 dark:text-green-400 font-semibold">{item.extended_support_discount}% OFF</span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Selected Addons */}
                  {item.selectedAddons && item.selectedAddons.length > 0 && item.addons && (
                    <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600">
                      <div className="space-y-3">
                        <p className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-3">
                          Selected Add-ons:
                        </p>
                        {item.selectedAddons.map((addonId, addonIndex) => {
                          const addon = item.addons.find(a => a.id === addonId);
                          return addon ? (
                            <div key={addonIndex} className="flex justify-between items-center pl-4">
                              <div className="flex items-center gap-3">
                                <div className="w-3 h-3 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full shadow-lg"></div>
                                <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                                  {addon.name}
                                </span>
                                {addon.short_description && (
                                  <span className="text-xs text-gray-500 dark:text-gray-400">
                                    - {addon.short_description}
                                  </span>
                                )}
                              </div>
                              <div className="text-right">
                                <span className="text-lg font-bold text-gray-900 dark:text-white">
                                  {formatPrice(addon.price || 0)}
                                </span>
                                {addon.original_price && addon.original_price !== addon.price && (
                                  <div className="text-sm text-gray-500 dark:text-gray-400">
                                    <span className="line-through">{formatPrice(addon.original_price)}</span>
                                    {addon.discount_percentage > 0 && (
                                      <span className="ml-1 text-green-600 dark:text-green-400 font-semibold">{addon.discount_percentage}% OFF</span>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          ) : null;
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )) : (
                <div className="text-center py-12">
                  <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-gray-400 to-gray-600 rounded-full flex items-center justify-center shadow-lg">
                    <Package className="w-12 h-12 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">No items to display</h3>
                  <p className="text-lg text-gray-500 dark:text-gray-400">
                    This order doesn't contain any product items.
                  </p>
                </div>
              )
              )}
            </div>
          </div>
        </div>

        {/* Payment Option Details */}
        {orderDetails.payment_option && (
          <div className="mt-12 card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-3xl shadow-xl border border-white/20 p-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center shadow-lg">
                <CreditCard className="w-6 h-6 text-white" />
              </div>
              Payment Option Details
            </h2>
            <div className="space-y-6">
              {/* Payment Option Type */}
              <div className="flex justify-between items-center p-4 card-3d bg-gradient-to-br from-gray-50 to-blue-50 dark:from-gray-800 dark:to-blue-900/20 backdrop-blur-md rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700">
                <span className="font-bold text-gray-700 dark:text-gray-300">Payment Option:</span>
                <span className="text-xl font-bold text-gray-900 dark:text-white capitalize">
                  {orderDetails.payment_option === 'full' ? 'Full Payment' : 
                   orderDetails.payment_option === 'advance' ? 'Split Payment' : 
                   orderDetails.payment_option === 'split_payment' ? 'Split Payment' :
                   orderDetails.payment_option === 'emi' ? 'EMI Payment' :
                   orderDetails.payment_option === 'installment' ? `${orderDetails.payment_option_details?.paymentData?.type === 'emi_payment' ? 'EMI' : 'Split'} Payment Installment` :
                   orderDetails.payment_option}
                </span>
              </div>

              {/* Payment Option Details */}
              {orderDetails.payment_option_details && (
                <div className="space-y-6">
                  {/* Check if this is an installment payment */}
                  {(() => {
                    console.log('🔍 ThankYou Page Debug - Full orderDetails:', orderDetails);
                    console.log('🔍 Payment Option:', orderDetails.payment_option);
                    console.log('🔍 Payment Option Details:', orderDetails.payment_option_details);
                    console.log('🔍 Is Installment Payment:', orderDetails.payment_option_details?.isInstallmentPayment);
                    console.log('🔍 Payment Data:', orderDetails.payment_option_details?.paymentData);
                    return null;
                  })()}
                  {orderDetails.payment_option === 'installment' && orderDetails.payment_option_details?.isInstallmentPayment ? (
                    /* EMI/Split Payment Installment Details */
                    <>
                      {/* Original Total Amount and EMI Calculations - From the original order reference */}
                      <OriginalOrderDetails 
                        paymentData={orderDetails.payment_option_details.paymentData}
                        formatPrice={formatPrice}
                        orderDetails={orderDetails}
                      />

                      {/* Current Installment Number */}
                      {orderDetails.payment_option_details.paymentData?.current_installment && (
                        <div className="flex justify-between items-center p-4 card-3d bg-gradient-to-br from-yellow-50 to-amber-50 dark:from-yellow-900/20 dark:to-amber-900/20 backdrop-blur-md rounded-2xl shadow-lg border border-yellow-200 dark:border-yellow-800">
                          <span className="font-bold text-yellow-700 dark:text-yellow-300">Current Installment:</span>
                          <span className="text-xl font-bold text-yellow-900 dark:text-yellow-100">
                            {orderDetails.payment_option_details.paymentData.current_installment} of {orderDetails.payment_option_details.paymentData?.total_installments || 1}
                          </span>
                        </div>
                      )}

                      {/* Current Installment Amount Paid */}
                      <div className="flex justify-between items-center p-4 card-3d bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 backdrop-blur-md rounded-2xl shadow-lg border border-purple-200 dark:border-purple-800">
                        <span className="font-bold text-purple-700 dark:text-purple-300">Current Installment Amount:</span>
                        <span className="text-xl font-bold text-purple-900 dark:text-purple-100">
                          {formatPrice(orderDetails.total || 0)}
                        </span>
                      </div>

                      {/* Next EMI Details */}
                      {(() => {
                        const paymentData = orderDetails.payment_option_details.paymentData;
                        const currentInstallment = parseInt(paymentData?.current_installment || 1);
                        const totalInstallments = parseInt(paymentData?.total_installments || 1);
                        const hasNextInstallment = currentInstallment < totalInstallments;
                        
                        // Calculate next installment date (add 1 month to current due date)
                        const calculateNextInstallmentDate = (currentDate) => {
                          if (!currentDate) return null;
                          try {
                            const date = new Date(currentDate);
                            date.setMonth(date.getMonth() + 1);
                            return date.toISOString().split('T')[0]; // Return YYYY-MM-DD format
                          } catch (error) {
                            console.log('Error calculating next installment date:', error);
                            return null;
                          }
                        };

                        const currentDueDate = paymentData?.payment_date || paymentData?.next_payment_date;
                        const nextInstallmentDate = calculateNextInstallmentDate(currentDueDate);
                        
                        // Use next_payment_amount if available, otherwise use current amount
                        const nextAmount = paymentData?.next_payment_amount || paymentData?.amount || orderDetails.total;
                        
                        console.log('🔍 Next Installment Debug:', {
                          currentInstallment,
                          totalInstallments,
                          hasNextInstallment,
                          currentDueDate,
                          nextInstallmentDate,
                          nextAmount,
                          paymentData
                        });
                        
                        return hasNextInstallment ? (
                          <div className="p-6 card-3d bg-gradient-to-br from-gray-50 to-blue-50 dark:from-gray-800 dark:to-blue-900/20 backdrop-blur-md rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700">
                            <h4 className="text-lg font-bold text-gray-800 dark:text-gray-200 mb-4">
                              Next Installment Details (Installment {currentInstallment + 1} of {totalInstallments})
                            </h4>
                            <div className="space-y-3 text-sm">
                              <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-600">
                                <span className="font-semibold text-gray-700 dark:text-gray-300">Next Due Date:</span>
                                <span className="font-bold text-gray-900 dark:text-gray-100">
                                  {nextInstallmentDate ? formatDate(nextInstallmentDate) : 'To be determined'}
                                </span>
                              </div>
                              <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-600">
                                <span className="font-semibold text-gray-700 dark:text-gray-300">Next Amount:</span>
                                <span className="font-bold text-gray-900 dark:text-gray-100">
                                  {formatPrice(nextAmount)}
                                </span>
                              </div>
                              <div className="flex justify-between py-2">
                                <span className="font-semibold text-gray-700 dark:text-gray-300">Payment Type:</span>
                                <span className="font-bold text-gray-900 dark:text-gray-100">
                                  {paymentData?.type === 'emi_payment' ? 'EMI Payment' : 'Split Payment'}
                                </span>
                              </div>
                            </div>
                          </div>
                        ) : (
                          currentInstallment >= totalInstallments ? (
                            <div className="p-6 card-3d bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 backdrop-blur-md rounded-2xl shadow-lg border border-green-200 dark:border-green-700">
                              <h4 className="text-lg font-bold text-green-800 dark:text-green-200 mb-3">
                                🎉 Final Installment Completed!
                              </h4>
                              <p className="text-green-700 dark:text-green-300">
                                This was the last installment for this order. All payments have been completed.
                              </p>
                            </div>
                          ) : null
                        );
                      })()}
                    </>
                  ) : (
                    /* Regular Payment Options */
                    <>
                      {/* Original Total Amount */}
                      {orderDetails.original_total_amount && (
                        <div className="flex justify-between items-center p-4 card-3d bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 backdrop-blur-md rounded-2xl shadow-lg border border-blue-200 dark:border-blue-800">
                          <span className="font-bold text-blue-700 dark:text-blue-300">Original Total Amount:</span>
                          <span className="text-xl font-bold text-blue-900 dark:text-blue-100">
                            {new Intl.NumberFormat('en-IN', {
                              style: 'currency',
                              currency: 'INR'
                            }).format(orderDetails.original_total_amount)}
                          </span>
                        </div>
                      )}

                      {/* Advance Amount */}
                      {orderDetails.advance_amount && orderDetails.payment_option !== 'full' && (
                        <div className="flex justify-between items-center p-4 card-3d bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 backdrop-blur-md rounded-2xl shadow-lg border border-green-200 dark:border-green-800">
                          <span className="font-bold text-green-700 dark:text-green-300">Advance Amount:</span>
                          <span className="text-xl font-bold text-green-900 dark:text-green-100">
                            {new Intl.NumberFormat('en-IN', {
                              style: 'currency',
                              currency: 'INR'
                            }).format(orderDetails.advance_amount)}
                          </span>
                        </div>
                      )}

                      {/* Remaining Amount */}
                      {orderDetails.remaining_amount && orderDetails.payment_option !== 'full' && (
                        <div className="flex justify-between items-center p-4 card-3d bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-900/20 dark:to-amber-900/20 backdrop-blur-md rounded-2xl shadow-lg border border-orange-200 dark:border-orange-800">
                          <span className="font-bold text-orange-700 dark:text-orange-300">Remaining Amount:</span>
                          <span className="text-xl font-bold text-orange-900 dark:text-orange-100">
                            {new Intl.NumberFormat('en-IN', {
                              style: 'currency',
                              currency: 'INR'
                            }).format(orderDetails.remaining_amount)}
                          </span>
                        </div>
                      )}

                      {/* Split Payment specific breakdown (After Demo / After Project) */}
                      {orderDetails.payment_option === 'split_payment' && (() => {
                        const originalTotal = parseFloat(
                          orderDetails.original_total_amount || 
                          orderDetails.payment_option_details?.original_total_amount || 
                          orderDetails.payment_option_details?.total_amount || 0
                        ) || 0;
                        const advanceAmount = parseFloat(
                          orderDetails.advance_amount || 
                          orderDetails.payment_option_details?.paid_amount || 0
                        ) || 0;
                        const currentSplitType = orderDetails.payment_option_details?.split_payment_type || orderDetails.splitPaymentType || '';
                        const currentPaymentAmount = parseFloat(orderDetails.total || 0) || 0;
                        const remainingAmount = parseFloat(orderDetails.remaining_amount || 0) || Math.max(0, originalTotal - (advanceAmount + currentPaymentAmount));

                        let afterDemoAmount = orderDetails.payment_option_details?.after_demo_amount;
                        let afterProjectAmount = orderDetails.payment_option_details?.after_project_amount;

                        if (afterDemoAmount === undefined || afterProjectAmount === undefined) {
                          if (currentSplitType === 'after_demo') {
                            afterDemoAmount = currentPaymentAmount;
                            afterProjectAmount = remainingAmount;
                          } else if (currentSplitType === 'after_project') {
                            afterProjectAmount = currentPaymentAmount;
                            afterDemoAmount = Math.max(0, originalTotal - (advanceAmount + currentPaymentAmount));
                          } else {
                            // Fallback split if type unknown: show remaining as a single line below
                            afterDemoAmount = undefined;
                            afterProjectAmount = undefined;
                          }
                        }

                        return (
                          <>
                            {typeof afterDemoAmount === 'number' && afterDemoAmount > 0 && (
                              <div className="flex justify-between items-center p-4 card-3d bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 backdrop-blur-md rounded-2xl shadow-lg border border-emerald-200 dark:border-emerald-800">
                                <span className="font-bold text-emerald-700 dark:text-emerald-300">After Demo Completion Amount:</span>
                                <span className="text-xl font-bold text-emerald-900 dark:text-emerald-100">
                                  {new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(afterDemoAmount)}
                                </span>
                              </div>
                            )}
                            {typeof afterProjectAmount === 'number' && afterProjectAmount > 0 && (
                              <div className="flex justify-between items-center p-4 card-3d bg-gradient-to-br from-violet-50 to-purple-50 dark:from-violet-900/20 dark:to-purple-900/20 backdrop-blur-md rounded-2xl shadow-lg border border-violet-200 dark:border-violet-800">
                                <span className="font-bold text-violet-700 dark:text-violet-300">After Project Completion Amount:</span>
                                <span className="text-xl font-bold text-violet-900 dark:text-violet-100">
                                  {new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(afterProjectAmount)}
                                </span>
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </>
                  )}

                  {/* EMI Details */}
                  {orderDetails.payment_option === 'emi' && orderDetails.payment_option_details?.emi_details && (
                    <div className="p-6 card-3d bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 backdrop-blur-md rounded-2xl shadow-lg border border-purple-200 dark:border-purple-800">
                      <h4 className="text-lg font-bold text-purple-800 dark:text-purple-200 mb-4">EMI Details</h4>
                      <div className="space-y-3 text-sm">
                        <div className="flex justify-between py-2 border-b border-purple-200 dark:border-purple-700">
                          <span className="font-semibold text-purple-700 dark:text-purple-300">EMI Tenure:</span>
                          <span className="font-bold text-purple-900 dark:text-purple-100">
                            {orderDetails.payment_option_details.emi_details.selectedEmiTenure} months
                          </span>
                        </div>
                        <div className="flex justify-between py-2 border-b border-purple-200 dark:border-purple-700">
                          <span className="font-semibold text-purple-700 dark:text-purple-300">Monthly EMI:</span>
                          <span className="font-bold text-purple-900 dark:text-purple-100">
                            {new Intl.NumberFormat('en-IN', {
                              style: 'currency',
                              currency: 'INR'
                            }).format(orderDetails.payment_option_details.emi_details.emiAmount || 0)}
                          </span>
                        </div>
                        <div className="flex justify-between py-2 border-b border-purple-200 dark:border-purple-700">
                          <span className="font-semibold text-purple-700 dark:text-purple-300">EMI Principal:</span>
                          <span className="font-bold text-purple-900 dark:text-purple-100">
                            {new Intl.NumberFormat('en-IN', {
                              style: 'currency',
                              currency: 'INR'
                            }).format(orderDetails.payment_option_details.emi_details.emiPrincipal || 0)}
                          </span>
                        </div>
                        <div className="flex justify-between py-2 border-b border-purple-200 dark:border-purple-700">
                          <span className="font-semibold text-purple-700 dark:text-purple-300">Processing Fee:</span>
                          <span className="font-bold text-purple-900 dark:text-purple-100">
                            {new Intl.NumberFormat('en-IN', {
                              style: 'currency',
                              currency: 'INR'
                            }).format(orderDetails.payment_option_details?.paymentOptionsConfig?.emi_processing_fee || 0)}
                          </span>
                        </div>
                        <div className="flex justify-between py-2 border-b border-purple-200 dark:border-purple-700">
                          <span className="font-semibold text-purple-700 dark:text-purple-300">Interest Rate:</span>
                          <span className="font-bold text-purple-900 dark:text-purple-100">
                            {orderDetails.payment_option_details?.paymentOptionsConfig?.emi_interest_rate || 0}% p.a.
                          </span>
                        </div>
                        <div className="flex justify-between py-2">
                          <span className="font-semibold text-purple-700 dark:text-purple-300">Total EMI Amount:</span>
                          <span className="font-bold text-purple-900 dark:text-purple-100">
                            {new Intl.NumberFormat('en-IN', {
                              style: 'currency',
                              currency: 'INR'
                            }).format(orderDetails.payment_option_details.emi_details.totalEmiAmount || 0)}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Applied Coupon */}
                  {orderDetails.payment_option_details?.appliedCoupon && (
                    <div className="flex justify-between items-center p-4 card-3d bg-gradient-to-br from-yellow-50 to-amber-50 dark:from-yellow-900/20 dark:to-amber-900/20 backdrop-blur-md rounded-2xl shadow-lg border border-yellow-200 dark:border-yellow-800">
                      <span className="font-bold text-yellow-700 dark:text-yellow-300">Applied Coupon:</span>
                      <span className="text-xl font-bold text-yellow-900 dark:text-yellow-100">
                        {orderDetails.payment_option_details.appliedCoupon.code} ({orderDetails.payment_option_details.appliedCoupon.discount}% OFF)
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Payment Status Notice for Offline Payments */}
        {orderDetails.paymentStatus === 'pending' && orderDetails.paymentMethod?.includes('Offline') && (
          <div className="mt-12 card-3d bg-gradient-to-br from-yellow-50 to-amber-50 dark:from-yellow-900/20 dark:to-amber-900/20 backdrop-blur-md rounded-3xl shadow-xl border border-yellow-200 dark:border-yellow-800 p-8">
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-gradient-to-br from-yellow-500 to-amber-600 rounded-full flex items-center justify-center shadow-lg">
                <Clock className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-yellow-900 dark:text-yellow-100 mb-4">
                  Payment Verification Required
                </h3>
                <p className="text-lg text-yellow-800 dark:text-yellow-200 mb-6 leading-relaxed">
                  Since you chose offline payment, your order will be processed once we receive and verify your payment. 
                  Please ensure you have completed the payment using the provided bank/UPI details.
                </p>
                <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-2xl shadow-lg border border-white/20 p-6">
                  <h4 className="text-lg font-bold text-yellow-900 dark:text-yellow-100 mb-4">What happens next?</h4>
                  <ul className="text-sm text-yellow-800 dark:text-yellow-200 space-y-2">
                    <li className="flex items-start space-x-2">
                      <span className="text-yellow-600 dark:text-yellow-400 font-bold">•</span>
                      <span>We'll verify your payment within 24-48 hours</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-yellow-600 dark:text-yellow-400 font-bold">•</span>
                      <span>You'll receive an email confirmation once payment is confirmed</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-yellow-600 dark:text-yellow-400 font-bold">•</span>
                      <span>Your order will be processed and shipped accordingly</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Rating Section */}
        <div className="mt-12 card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-3xl shadow-xl border border-white/20 p-8">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-yellow-500 to-amber-600 rounded-full flex items-center justify-center shadow-lg">
              <Star className="w-6 h-6 text-white" />
            </div>
            Rate Your Experience
          </h2>
          
          {/* Show submitted rating if exists */}
          {orderDetails.rating ? (
            <div className="space-y-6">
              <div className="flex items-center space-x-3">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star
                    key={star}
                    className={`w-10 h-10 ${
                      star <= orderDetails.rating
                        ? 'text-yellow-500 fill-current'
                        : 'text-gray-300'
                    }`}
                  />
                ))}
                <span className="ml-3 text-2xl font-bold text-gray-900 dark:text-white">
                  {orderDetails.rating}/5
                </span>
              </div>
              {orderDetails.review && (
                <div className="card-3d bg-gradient-to-br from-gray-50 to-blue-50 dark:from-gray-800 dark:to-blue-900/20 backdrop-blur-md rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 p-6">
                  <p className="text-lg text-gray-700 dark:text-gray-300 italic leading-relaxed">
                    "{orderDetails.review}"
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-3 font-semibold">
                    Submitted on {formatDate(orderDetails.review_date)}
                  </p>
                </div>
              )}
              <div className="text-green-600 dark:text-green-400 text-lg font-bold">
                ✓ Thank you for your feedback!
              </div>
            </div>
          ) : (
            /* Rating form */
            <div className="space-y-6">
              <div className="flex items-center space-x-3">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    onClick={() => setRating(star)}
                    onMouseEnter={() => setHoverRating(star)}
                    onMouseLeave={() => setHoverRating(0)}
                    className="text-3xl text-gray-300 hover:text-yellow-400 transition-all duration-300 hover:scale-110"
                  >
                    <Star
                      className={`w-12 h-12 ${
                        star <= (hoverRating || rating)
                          ? 'text-yellow-500 fill-current'
                          : 'text-gray-300'
                      }`}
                    />
                  </button>
                ))}
              </div>
              <textarea
                value={review}
                onChange={(e) => setReview(e.target.value)}
                placeholder="Share your experience with us (optional)"
                className="w-full px-6 py-4 border-2 border-gray-300 dark:border-gray-600 rounded-2xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-lg transition-all duration-300 resize-none"
                rows={4}
              />
              <button
                onClick={handleRatingSubmit}
                disabled={rating === 0}
                className="card-3d bg-gradient-to-r from-yellow-500 to-amber-600 hover:from-yellow-600 hover:to-amber-700 disabled:from-gray-400 disabled:to-gray-500 text-white px-8 py-4 rounded-2xl font-bold transition-all duration-300 disabled:cursor-not-allowed hover:scale-105 shadow-glow"
              >
                Submit Rating
              </button>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="mt-12 flex flex-col sm:flex-row gap-6 justify-center">
          <button
            onClick={handleBackToHome}
            className="card-3d bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-10 py-4 rounded-2xl font-bold transition-all duration-300 transform hover:scale-105 shadow-glow flex items-center justify-center space-x-3"
          >
            <Home className="w-6 h-6" />
            <span>Back to Home</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ThankYou; 